/**
 * The vocabulary a parsed statement comes back in, and how each word reads.
 *
 * Every status the import API returns is a plain String rather than an enum, so
 * the values are written down once here — with the tone each takes and the
 * words a person should see instead of the constant. A screen never compares a
 * raw status string, and an unrecognised value degrades to a neutral chip
 * showing itself rather than to a blank cell, so a status the backend adds
 * later is visible rather than silently dropped.
 */

/* ── how well a scheme matched the security master ──────────── */

/**
 * The statement names a scheme; the app can only book a security it holds in
 * its master. This is how well those two were reconciled, and it is the single
 * thing worth reading on the whole preview: a scheme that matched the WRONG
 * security books a client into a fund they do not own.
 */
export const MATCH = {
  MATCHED: { label: 'Matched', tone: 'green', rank: 3 },
  MATCHED_WITH_WARNING: { label: 'Check match', tone: 'amber', rank: 1 },
  AMBIGUOUS: { label: 'Needs a choice', tone: 'red', rank: 0 },
  UNMATCHED: { label: 'No match', tone: 'grey', rank: 2 },
};

export const matchOf = (status) => MATCH[status] ?? { label: status, tone: 'grey', rank: 4 };

/** Will this scheme be booked as things stand? */
export const willBook = (status) => status === 'MATCHED' || status === 'MATCHED_WITH_WARNING';

/** A scheme the advisor has to act on before it can be booked. */
export const needsChoice = (status) => status === 'AMBIGUOUS';

/**
 * The schemes that need the reader's attention — a count, not an order.
 *
 * The table itself is left in the order the backend returned, which is stable
 * across rechecks: re-sorting live would move a row the moment it was
 * resolved, taking the row being worked on out from under the reader. Sorting
 * is the reader's to ask for, by clicking a column.
 */
export const needsAttention = (schemes) =>
  (schemes ?? []).filter((s) => matchOf(s.status).rank <= 1);

/* ── what booking the statement would change ────────────────── */

/**
 * A position against what the portfolio already holds. `NEW` and `CLOSED` are
 * the statement changing something; `UNCHANGED` is a re-import of a folio
 * already booked, which is the common case a few months in.
 */
export const HOLDING_DIFF = {
  NEW: { label: 'New', tone: 'green' },
  UNCHANGED: { label: 'Unchanged', tone: 'grey' },
  CLOSED: { label: 'Closed', tone: 'amber' },
  UNMATCHED: { label: 'Not booked', tone: 'red' },
};

export const holdingDiffOf = (status) => HOLDING_DIFF[status] ?? { label: status, tone: 'grey' };

/**
 * A ledger row against what is already booked.
 *
 * `SYNTHETIC` is the one that needs explaining: an opening position the ledger
 * implies but the statement never printed, because the folio's history starts
 * before the statement period. It is re-derived on every apply rather than
 * matched, so it reads SYNTHETIC again on a re-import while the real rows
 * beside it read ALREADY_BOOKED.
 */
export const TXN_DIFF = {
  NEW: { label: 'New', tone: 'green' },
  ALREADY_BOOKED: { label: 'Already booked', tone: 'grey' },
  SYNTHETIC: { label: 'Derived opening', tone: 'blue' },
};

export const txnDiffOf = (status) => TXN_DIFF[status] ?? { label: status, tone: 'grey' };

/* ── the statement's own transaction vocabulary ─────────────── */

export const TXN_TYPE = {
  PURCHASE: 'Purchase',
  PURCHASE_SIP: 'SIP purchase',
  REDEMPTION: 'Redemption',
  REDEMPTION_SWP: 'SWP redemption',
  SWITCH_IN: 'Switch in',
  SWITCH_OUT: 'Switch out',
  MERGER_IN: 'Merger in',
  MERGER_OUT: 'Merger out',
  DIVIDEND_REINVEST: 'IDCW reinvested',
  DIVIDEND_PAYOUT: 'IDCW payout',
  OPENING_BALANCE: 'Opening balance',
  ADJUSTMENT: 'Adjustment',
};

/** `PURCHASE_SIP` → "SIP purchase"; anything unlisted reads as itself. */
export const txnTypeLabel = (type) =>
  TXN_TYPE[type] ?? String(type ?? '').replace(/_/g, ' ').toLowerCase();

/* ── what the parse thought of the statement ────────────────── */

/**
 * The checks the parser ran against the statement's own arithmetic.
 *
 * An `error` means the parse disagrees with the statement — opening units plus
 * movements did not reach the printed closing balance, or there was no closing
 * balance to reach. That scheme is held back from the booking rather than
 * written wrong. Everything else is a warning: worth reading, not worth
 * stopping for.
 */
export const ISSUE = {
  NO_FOLIOS: 'No folios could be read from the statement',
  UNIT_RECONCILIATION: 'Units do not reconcile',
  CLOSING_MISSING: 'No closing balance',
  MARKET_VALUE_CHECK: 'Market value disagrees with units × NAV',
  SUMMARY_TOTAL_CHECK: 'Scheme values do not add up to the portfolio total',
  SUMMARY_COST_CHECK: 'Scheme costs do not add up to the portfolio total',
  SIGN_CHECK: 'A figure carries an unexpected sign',
  ISIN_MISSING: 'No ISIN',
  ISIN_FORMAT: 'ISIN is not shaped like a mutual fund ISIN',
  PAN_FORMAT: 'PAN is malformed',
  PARSE_WARNING: 'The parser flagged this row',
};

export const issueLabel = (code) => ISSUE[code] ?? String(code ?? '').replace(/_/g, ' ');

export const issueTone = (severity) =>
  (String(severity).toLowerCase() === 'error' ? 'red' : 'amber');

export const isBlocking = (issue) => String(issue?.severity).toLowerCase() === 'error';

/* ── how a scheme was matched, and where the statement came from ── */

export const MATCHED_VIA = {
  ISIN: 'by ISIN',
  RTA_CODE: 'by RTA code',
  OVERRIDE: 'chosen by you',
};

export const matchedViaLabel = (via) => MATCHED_VIA[via] ?? null;

/* Spelled as the backend sends them — CSV_HOLDINGS, not HOLDINGS_CSV. */
export const SOURCE_FORMAT = {
  CAS_PDF: 'CAS PDF',
  CAS_TEXT: 'CAS text',
  CSV_HOLDINGS: 'Holdings CSV',
  CSV_TRANSACTIONS: 'Transactions CSV',
  CAS_JSON: 'Parsed CAS',
  /* A workbook of scheme names and values. Named for what it is rather than for
     the file extension, because the parser reports one `SHEET` for .xlsx and
     .xls alike — and without this the screen printed the enum itself. */
  SHEET: 'Holdings sheet',
};

export const ISSUER = {
  CAMS_KFINTECH: 'CAMS + KFintech',
  NSDL: 'NSDL',
  CDSL: 'CDSL',
};

export const sourceLabel = (upload) => {
  const format = SOURCE_FORMAT[upload?.sourceFormat] ?? upload?.sourceFormat ?? 'Statement';
  const issuer = ISSUER[upload?.issuer] ?? upload?.issuer;
  return issuer ? `${format} · ${issuer}` : format;
};

/**
 * Whether the upload takes a chosen start date. Only a holdings sheet does: it
 * prints positions with no dates, so every fund is booked as bought on one day
 * the reader picks. Every other format dates its own rows, and the backend
 * refuses `startDate` for them outright.
 */
export const takesStartDate = (upload) => upload?.sourceFormat === 'SHEET';

/** A statement with no ledger cannot give cost basis, realised gains or XIRR. */
export const isDetailed = (upload) => upload?.casType === 'DETAILED';

/* ── who the statement belongs to ───────────────────────────── */

/**
 * How the statement's own holder was resolved against the platform.
 *
 * The client is identified from the DOCUMENT — its PAN, then its mobile or
 * email — rather than chosen on screen, so this is the answer to "whose
 * portfolio is this about to become", and for half these values the answer is
 * "nobody's, yet".
 *
 * `books` is the one that matters: four of these REFUSE the apply, and the
 * screen has to say so before the button is pressed rather than let the server
 * answer. `effect` is what pressing it actually does, which for a new client is
 * a great deal more than writing a portfolio.
 */
export const INVESTOR_STATUS = {
  SELF_UPLOAD: {
    label: 'Your own book',
    tone: 'blue',
    books: true,
    effect: 'Books into your own portfolio — you are not in a firm, so there is no client to file this under.',
  },
  NEW_CLIENT: {
    label: 'New client',
    tone: 'amber',
    books: true,
    creates: true,
    effect: 'Creates the user, a BD account carrying their PAN, an approved link to your firm, a goal and the portfolio.',
  },
  EXISTING_CLIENT: {
    label: 'Existing client',
    tone: 'green',
    books: true,
    effect: 'Merges into this client’s existing goal portfolio.',
  },
  EXISTING_USER_LINKED: {
    label: 'Existing user — will be linked',
    tone: 'amber',
    books: true,
    links: true,
    effect: 'This person already has a platform account but no link to your firm. Booking links them to it, approved, and then writes the portfolio.',
  },
  NEEDS_CONTACT: {
    label: 'Needs an email or phone',
    tone: 'amber',
    books: false,
    needsContact: true,
    effect: 'A new investor with no email and no mobile on the statement. Supply either and this becomes a new client.',
  },
  CONFLICT: {
    label: 'Conflicting identity',
    tone: 'red',
    books: false,
    effect: 'The mobile and email point at two different people, or the matched user’s PAN does not agree with the statement’s.',
  },
  OTHER_FIRM: {
    label: 'Another firm’s client',
    tone: 'red',
    books: false,
    effect: 'This investor is already linked to a different organisation.',
  },
  SELF: {
    label: 'Your own statement',
    tone: 'red',
    books: false,
    effect: 'This statement is yours, not a client’s — it cannot be booked as somebody else’s portfolio.',
  },
};

/** An unknown status is treated as un-bookable: the server decides, not a guess. */
export const investorStatusOf = (status) =>
  INVESTOR_STATUS[status] ?? {
    label: String(status ?? 'Unresolved').replace(/_/g, ' ').toLowerCase(),
    tone: 'grey',
    books: false,
    effect: 'This statement could not be resolved to a person, so it cannot be booked.',
  };

/** Will booking bring a new person onto the platform? */
export const willCreateClient = (investor) =>
  Boolean(investorStatusOf(investor?.status).creates);

/** Does the investor still need contact details before they can be created? */
export const needsContact = (investor) =>
  Boolean(investorStatusOf(investor?.status).needsContact);

/**
 * Why the apply would be refused, or null when it would go through.
 *
 * NEEDS_CONTACT is the one that the reader can clear themselves — an email or a
 * mobile turns it into a new client — so it stops being a blocker the moment
 * either is supplied.
 */
export function investorBlocker(investor, { hasContact = false } = {}) {
  if (!investor) return null;
  const state = investorStatusOf(investor.status);
  if (state.books) return null;
  if (state.needsContact && hasContact) return null;
  return investor.message || state.effect;
}

/** Which key found the person — PAN, phone, email, or the target-user header. */
export const matchedByLabel = (by) => {
  const s = String(by ?? '').toUpperCase();
  if (s === 'PAN') return 'matched by PAN';
  if (s === 'PHONE') return 'matched by mobile';
  if (s === 'EMAIL') return 'matched by email';
  if (s === 'REQUESTED') return 'named by the client you are importing for';
  return by ? `matched by ${s.toLowerCase().replace(/_/g, ' ')}` : null;
};

/* ── the distributor an ARN names ───────────────────────────── */

/**
 * Whose book a folio sits on — the question a CAS exists to answer for a
 * distributor, and the only place another distributor's holdings are visible.
 *
 * `advisor` is the statement's raw advisor field: an ARN, or "DIRECT" where
 * nobody is paid trail. A holding on another ARN is the transfer-in
 * conversation; a direct holding is a different one, because moving it costs
 * the client the direct plan's expense ratio.
 */
export const arnBucket = (advisor, ownArn) => {
  const raw = String(advisor ?? '').trim();
  if (!raw || /^(direct|-|—|n\/?a)$/i.test(raw)) return 'direct';
  const norm = (v) => String(v ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();
  return norm(ownArn) && norm(raw) === norm(ownArn) ? 'own' : 'other';
};

export const BUCKET = {
  own: { label: 'On your ARN', tone: 'green', color: 'var(--green)' },
  other: { label: 'Another distributor', tone: 'amber', color: 'var(--amber)' },
  direct: { label: 'Direct', tone: 'grey', color: 'color-mix(in srgb,var(--ink2) 45%,var(--panel))' },
};

export const BUCKET_ORDER = ['own', 'other', 'direct'];

/** The ARN mentioned in free text (a seat's job title), or null. */
export const arnIn = (text) => {
  const m = String(text ?? '').match(/ARN[-\s]*(\w+)/i);
  return m ? `ARN-${m[1].toUpperCase()}` : null;
};

/* ── the JSON scalars ───────────────────────────────────────── */

/**
 * `totals` is a JSON scalar whose figures arrive as strings, so a value is read
 * through here rather than used raw — `"494960.38" * 1` is a number, but
 * `"494960.38"` in a sum is a concatenation.
 */
export const totalNum = (totals, key) => {
  const v = totals?.[key];
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The scheme a skipped-row refers to, whatever spelling it arrived in.
 *
 * These lists are JSON scalars, so the server's own key names come through
 * untouched — `scheme_key` rather than `schemeKey` — and a plain list of keys
 * is just as valid as a list of objects. Read through here so the screen never
 * depends on which.
 */
export const skippedKeyOf = (row) => {
  if (row == null) return null;
  if (typeof row === 'string') return row;
  return row.scheme_key ?? row.schemeKey ?? row.scheme ?? row.key ?? null;
};

/** Why a scheme was left out, in words rather than a constant. */
export const SKIP_REASON = {
  UNMATCHED: 'No matching security in the master',
  AMBIGUOUS: 'More than one candidate and none was chosen',
  VALIDATION_ERROR: 'The parse disagreed with the statement’s own arithmetic',
  SKIPPED: 'You chose not to book it',
  USER_SKIPPED: 'You chose not to book it',
  NO_SECURITY: 'No matching security in the master',
};

export const skipReasonOf = (row) => {
  const code = typeof row === 'string' ? null : (row?.reason ?? row?.status ?? null);
  /* A bare key carries no reason, and the only thing that produces one is a
     scheme nothing matched — so it reads the same as an explicit UNMATCHED
     rather than inventing a second phrase for one situation. */
  if (!code) return SKIP_REASON.UNMATCHED;
  return SKIP_REASON[code] ?? String(code).replace(/_/g, ' ').toLowerCase();
};

/** Reconciliation rows the ledger and the printed closing balance disagree on. */
export const adjustedRows = (reconciliation) =>
  (Array.isArray(reconciliation) ? reconciliation : []).filter((r) => r?.adjusted != null);
